export const MEDSPARECORD_BOOKING_HUB = "https://medsparecord.com/book?step=0";
const MEDSPARECORD_DIRECT_BOOKING_BASE = "https://medsparecord.com/book";

export const TREATMENT_BOOKING_SLUGS = {
  botox: "botox",
  lipFiller: "lip-filler",
  dermalFiller: "dermal-filler",
  cheekFiller: "cheek-filler",
  jawlineFiller: "jawline-filler",
  tearTrough: "tear-trough",
  kybella: "kybella",
  pdoThreads: "pdo-threads",
  co2Laser: "co2-laser",
  duovive: "duovive",
  rfMicroneedling: "rf-microneedling",
  collagenBoostMicroneedling: "collagen-boost-microneedling",
  microneedlingPrp: "microneedling-prp",
  ipl: "ipl",
  picosecond: "picosecond",
  tattooRemoval: "tattoo-removal",
  hydraglow: "hydraglow",
  chemicalPeel: "chemical-peel",
  prpHair: "prp-hair",
  shockwave: "shockwave",
  prpJoint: "prp-joint",
  weightLoss: "weight-loss",
  xerf: "xerf-consult",
  botoxMembership: "botox-membership",
  // Built in parallel on medsparecord (2026-10-07); the ad page links to it directly.
  facialBalancing: "facial-balancing",
} as const;

export type TreatmentBookingKey = keyof typeof TREATMENT_BOOKING_SLUGS;

const DIRECT_BOOKING_EXCEPTIONS = new Set<TreatmentBookingKey>(["xerf", "botoxMembership", "co2Laser"]);

export const TREATMENT_BOOKING_URLS = Object.fromEntries(
  Object.entries(TREATMENT_BOOKING_SLUGS).map(([key, slug]) => [
    key,
    DIRECT_BOOKING_EXCEPTIONS.has(key as TreatmentBookingKey)
      ? `${MEDSPARECORD_DIRECT_BOOKING_BASE}/${slug}`
      : MEDSPARECORD_BOOKING_HUB,
  ]),
) as { [K in TreatmentBookingKey]: string };

/**
 * Ad landing pages (/lp/*) always book straight into their procedure — owner
 * ruling 2026-10-07: every patient ad lands on a noindex ad page, and that
 * page's booking link is the procedure-specific medsparecord link. Service
 * pages keep the generic hub (2026-08-28 ruling) except the exceptions above.
 */
export const AD_LP_BOOKING_URLS = Object.fromEntries(
  Object.entries(TREATMENT_BOOKING_SLUGS).map(([key, slug]) => [key, `${MEDSPARECORD_DIRECT_BOOKING_BASE}/${slug}`]),
) as { [K in TreatmentBookingKey]: string };

/** Offer-specific medsparecord links used by the $-offer ad pages. */
export const AD_LP_OFFER_BOOKING_URLS = {
  rfMicroneedlingIntro: `${MEDSPARECORD_DIRECT_BOOKING_BASE}/rf-microneedling-intro`,
  rfMicroneedlingSeries: `${MEDSPARECORD_DIRECT_BOOKING_BASE}/rf-microneedling-series`,
  microneedlingIntro: `${MEDSPARECORD_DIRECT_BOOKING_BASE}/microneedling-intro`,
  microneedlingSeries: `${MEDSPARECORD_DIRECT_BOOKING_BASE}/microneedling-series`,
} as const;

export function getTreatmentBookingUrl(
  treatment?: TreatmentBookingKey,
): string {
  return treatment ? TREATMENT_BOOKING_URLS[treatment] : MEDSPARECORD_BOOKING_HUB;
}

export type RouteBookingMatch = "exact-service" | "closest-category" | "closest-concern";

export type RouteTreatmentBookingConfig = {
  treatmentName: string;
  shortName: string;
  primaryHref: string;
  match: RouteBookingMatch;
  injectSticky?: boolean;
};

const directRoute = (
  treatmentName: string,
  shortName: string,
  treatment: TreatmentBookingKey,
  injectSticky = false,
): RouteTreatmentBookingConfig => ({
  treatmentName,
  shortName,
  primaryHref: DIRECT_BOOKING_EXCEPTIONS.has(treatment)
    ? TREATMENT_BOOKING_URLS[treatment]
    : MEDSPARECORD_BOOKING_HUB,
  match: DIRECT_BOOKING_EXCEPTIONS.has(treatment) ? "exact-service" : "closest-category",
  injectSticky,
});

const adRoute = (
  treatmentName: string,
  shortName: string,
  treatment: TreatmentBookingKey,
  injectSticky = false,
): RouteTreatmentBookingConfig => ({
  treatmentName,
  shortName,
  primaryHref: AD_LP_BOOKING_URLS[treatment],
  match: "exact-service",
  injectSticky,
});

const closestRoute = (
  treatmentName: string,
  shortName: string,
  slug: string,
  match: Exclude<RouteBookingMatch, "exact-service">,
  injectSticky = false,
): RouteTreatmentBookingConfig => ({
  treatmentName,
  shortName,
  primaryHref: MEDSPARECORD_BOOKING_HUB,
  match,
  injectSticky,
});

/**
 * Single source of truth for the treatment-specific booking choice displayed
 * beside the generic booking hub on each service and paid treatment page.
 * Closest category/concern routes are limited to values recognized by the
 * public MedSpaRecord BookingDeepLink component.
 */
export const ROUTE_TREATMENT_BOOKING_CONFIG: Record<string, RouteTreatmentBookingConfig> = {
  "/face-skin-rejuvenation": closestRoute("Face & Skin Rejuvenation", "Skin Treatment", "skin-laxity", "closest-concern"),
  "/rf-microneedling": directRoute("RF Microneedling", "RF Microneedling", "rfMicroneedling"),
  "/fractional-co2": directRoute("CO2 Laser Resurfacing", "CO2 Laser", "co2Laser"),
  "/duovive": directRoute("DuoVive Skin Resurfacing", "DuoVive", "duovive"),
  "/dpl-photofacial": directRoute("IPL Photofacial", "IPL", "ipl"),
  "/pico-laser": directRoute("PicoLaser", "PicoLaser", "picosecond"),
  "/aquafacial": directRoute("AquaFacial", "AquaFacial", "hydraglow"),
  "/chemical-peels": directRoute("Chemical Peel", "Chemical Peel", "chemicalPeel"),
  "/hifu": closestRoute("HIFU Skin Tightening", "HIFU", "skin-laxity", "closest-concern"),
  "/xerf-skin-tightening": directRoute("XERF Skin Tightening", "XERF", "xerf"),
  "/skin-analysis": closestRoute("Skin Analysis", "Skin Consultation", "skin-laxity", "closest-concern", true),
  "/ai-skin-analysis": closestRoute("AI Skin Analysis", "Skin Consultation", "skin-laxity", "closest-concern", true),
  "/stretch-mark": directRoute("Stretch Mark Treatment", "Stretch Mark Treatment", "rfMicroneedling"),
  "/skin-laxity": closestRoute("Skin Laxity Treatment", "Skin Tightening", "skin-laxity", "closest-concern"),
  "/acne-scars": closestRoute("Acne Scar Treatment", "Acne Scar Treatment", "acne-scars", "closest-concern"),
  "/acne-breakouts": closestRoute("Acne Treatment", "Acne Treatment", "acne", "closest-concern", true),
  "/enlarged-pores": directRoute("Pore Refinement", "Pore Refinement", "collagenBoostMicroneedling"),
  "/pigmentation": closestRoute("Pigmentation Treatment", "Pigmentation Treatment", "pigmentation", "closest-concern"),
  "/fine-lines-wrinkles": closestRoute("Fine Lines & Wrinkles", "Wrinkle Treatment", "wrinkles", "closest-concern"),
  "/redness-rosacea": closestRoute("Redness & Rosacea Treatment", "Rosacea Treatment", "rosacea", "closest-concern"),
  "/dark-circles": directRoute("Under-Eye Rejuvenation", "Under-Eye Treatment", "tearTrough"),
  "/facial-volume": directRoute("Facial Volume Restoration", "Filler Consultation", "dermalFiller"),
  "/facial-balancing": directRoute("Facial Balancing", "Facial Balancing Consultation", "facialBalancing"),
  "/injectables": closestRoute("Injectable Treatments", "Injectables", "injectables", "closest-category"),
  "/botox": directRoute("Neuromodulator Treatment", "Neuromodulators", "botox"),
  "/dermal-fillers": directRoute("Dermal Fillers", "Dermal Fillers", "dermalFiller"),
  "/lip-fillers": directRoute("Lip Fillers", "Lip Fillers", "lipFiller"),
  "/kybella": directRoute("Kybella", "Kybella", "kybella"),
  "/pdo-threads": directRoute("PDO Threads", "PDO Threads", "pdoThreads"),
  "/biostimulators": closestRoute("Biostimulator Consultation", "Biostimulators", "injectables", "closest-category"),
  "/body-contouring": closestRoute("Body Contouring", "Body Contouring", "body-contouring", "closest-concern"),
  "/body-sculpting": closestRoute("Body Sculpting", "Body Sculpting", "body-contouring", "closest-concern"),
  "/renasculpt": closestRoute("RenaSculpt", "RenaSculpt", "body-contouring", "closest-concern"),
  "/cryolipolysis": closestRoute("Cryolipolysis", "Cryolipolysis", "body-contouring", "closest-concern"),
  "/diode-laser-lipolysis": closestRoute("Diode Laser Lipolysis", "Laser Lipolysis", "body-contouring", "closest-concern"),
  "/stubborn-body-fat": closestRoute("Stubborn Body Fat Treatment", "Body Contouring", "body-contouring", "closest-concern"),
  "/hair-growth": directRoute("PRP Hair Restoration", "PRP Hair Restoration", "prpHair"),
  "/lllt-hair-restoration": closestRoute("LLLT Hair Restoration", "Hair Restoration", "hair-growth", "closest-concern"),
  "/areslite-hair-removal": closestRoute("Laser Hair Removal", "Laser Hair Removal", "laser-hair-removal", "closest-category"),
  "/intimate-wellness": closestRoute("Intimate Wellness", "Private Consultation", "intimate-wellness", "closest-concern"),
  "/vaginal-rejuvenation": closestRoute("Vaginal Rejuvenation", "Private Consultation", "intimate-wellness", "closest-concern"),
  "/prp-sexual-wellness": closestRoute("PRP Sexual Wellness", "Private Consultation", "intimate-wellness", "closest-concern"),
  "/pelvic-floor": closestRoute("Pelvic Floor Therapy", "Pelvic Floor Therapy", "intimate-wellness", "closest-concern"),
  "/pain-regenerative-medicine": directRoute("Pain & Regenerative Medicine", "Pain Treatment", "shockwave"),
  "/weight-loss": directRoute("Medical Weight Loss", "Weight Loss", "weightLoss"),
  "/wellness": closestRoute("Wellness Consultation", "Wellness Consultation", "wellness", "closest-category"),
  "/hormone-optimization": closestRoute("Hormone Therapy Consultation", "Hormone Consultation", "wellness", "closest-category", true),
  "/peptide-therapy": closestRoute("Peptide Therapy Consultation", "Peptide Consultation", "wellness", "closest-category"),
  "/functional-lab-testing": closestRoute("Functional Lab Consultation", "Lab Consultation", "wellness", "closest-category", true),
  "/membership": directRoute("Botox Membership", "Botox Membership", "botoxMembership", true),
  "/specials": closestRoute("Specials & New", "a Consultation", "wellness", "closest-category"),
  "/lp/botox-dallas": adRoute("Neuromodulator Treatment", "Neuromodulators", "botox"),
  "/lp/dermal-fillers-dallas": adRoute("Dermal Fillers", "Dermal Fillers", "facialBalancing"),
  "/lp/co2-laser-dallas": adRoute("CO2 Laser Resurfacing", "CO2 Laser", "co2Laser"),
  "/lp/rf-microneedling-dallas": adRoute("RF Microneedling", "RF Microneedling", "rfMicroneedling"),
  "/lp/rf-microneedling-ads": adRoute("RF Microneedling", "RF Microneedling", "rfMicroneedling"),
  "/lp/ipl-photofacial-dallas": adRoute("IPL Photofacial", "IPL", "ipl"),
  "/lp/aquafacial-dallas": adRoute("AquaFacial", "AquaFacial", "hydraglow"),
  "/lp/aquafacial-red-light-dallas": adRoute("AquaFacial + Red Light", "AquaFacial", "hydraglow"),
  "/lp/prp-hair-dallas": adRoute("PRP Hair Restoration", "PRP Hair Restoration", "prpHair"),
  "/lp/microneedling-dallas": adRoute("Collagen Boost Microneedling", "Microneedling", "collagenBoostMicroneedling"),
  "/lp/tox-membership-dallas": adRoute("Botox Membership", "Botox Membership", "botoxMembership"),
  "/lp/facial-balancing-dallas": adRoute("Facial Balancing", "Facial Balancing", "facialBalancing"),
  // Same page, liquid-facelift message match; books the same free facial-balancing consult.
  "/lp/liquid-facelift-dallas": adRoute("Liquid Facelift", "Liquid Facelift", "facialBalancing"),
  "/lp/laser-hair-removal-dallas": closestRoute("Laser Hair Removal", "Laser Hair Removal", "laser-hair-removal", "closest-category"),
};

export function getRouteTreatmentBookingConfig(route: string): RouteTreatmentBookingConfig | undefined {
  return ROUTE_TREATMENT_BOOKING_CONFIG[route.split("?")[0]];
}
